import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _toArray from "@babel/runtime/helpers/toArray";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.array.concat.js";
import { useReducer } from 'react';
var isTypedAction = function isTypedAction(action) {
  return !!action && 'type' in action && (action.type === 'UNDO' || action.type === 'REDO' || action.type === 'UNDO_ALL' || action.type === 'RESET');
};

/**
 * Enhances React's built-in `useReducer` hook with an undo history.
 *
 * Use like a regular `useReducer` hook:
 * ```
 * const [state, dispatch, history] = useUndoReducer(reducer, initialState);
 * ```
 *
 * Call `dispatch({type: 'UNDO'})` to undo, `dispatch({type: 'REDO'})` to redo,
 * or `dispatch({type: 'UNDO_ALL'})` to return the state to its initial shape but keep the history.
 * Call `dispatch({type: 'RESET'})` to reset the state and clear the history.
 *
 * Check `history.past.length` or `history.future.length` to check if there are
 * entries in the undo or redo history. You can get the initial state from `history.initial`.
 *
 * Code taken from: https://gist.github.com/X-Y/028284a2fd7091d2d3fa3034e829f3d7
 */

function useUndoReducer(reducer, initialState, _ref) {
  var _ref$shouldSkipHistor = _ref.shouldSkipHistoryEntry,
    shouldSkipHistoryEntry = _ref$shouldSkipHistor === void 0 ? function () {
      return false;
    } : _ref$shouldSkipHistor;
  var initialHistory = {
    initial: initialState,
    past: [],
    present: initialState,
    future: []
  };
  var undoReducer = function undoReducer(state, action) {
    if (isTypedAction(action)) {
      if (action.type === 'UNDO') {
        var _state$past = _toArray(state.past),
          newPresent = _state$past[0],
          past = _state$past.slice(1);
        return {
          initial: initialState,
          past: past,
          present: newPresent,
          future: [state.present].concat(_toConsumableArray(state.future))
        };
      }
      if (action.type === 'REDO') {
        var _state$future = _toArray(state.future),
          _newPresent = _state$future[0],
          future = _state$future.slice(1);
        return {
          initial: initialState,
          past: [state.present].concat(_toConsumableArray(state.past)),
          present: _newPresent,
          future: future
        };
      }
      if (action.type === 'UNDO_ALL') {
        var _newPresent2 = state.past[state.past.length - 1];
        return {
          initial: initialState,
          past: [],
          present: _newPresent2,
          future: [state.present].concat(_toConsumableArray(state.past))
        };
      }
      if (action.type === 'RESET') {
        return initialHistory;
      }
      throw "shouldn't be here";
    } else {
      var _newPresent3 = reducer(state.present, action);
      var skipOldPresent = shouldSkipHistoryEntry(state.present);
      return {
        initial: initialState,
        past: skipOldPresent ? state.past : [state.present].concat(_toConsumableArray(state.past)),
        present: _newPresent3,
        future: []
      };
    }
  };
  var _useReducer = useReducer(undoReducer, initialHistory),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    state = _useReducer2[0],
    dispatch = _useReducer2[1];
  return [state.present, dispatch, state];
}
export default useUndoReducer;